export const getCameraErrorMessage = (error, { fallbackLabel = 'Usa la fotocamera del dispositivo.' } = {}) => {
  if (typeof window !== 'undefined' && !window.isSecureContext) return `La fotocamera web richiede HTTPS. ${fallbackLabel}`;
  if (error?.name === 'NotAllowedError' || error?.name === 'SecurityError') return `Permesso fotocamera negato. Abilitalo nel browser oppure ${fallbackLabel.toLowerCase()}`;
  if (error?.name === 'NotFoundError' || error?.name === 'DevicesNotFoundError') return 'Nessuna fotocamera disponibile su questo dispositivo.';
  if (error?.name === 'NotReadableError' || error?.name === 'TrackStartError') return 'La fotocamera è occupata da un’altra applicazione o non è accessibile.';
  return `Impossibile avviare la fotocamera. ${fallbackLabel}`;
};

export const releaseCameraStream = (stream, videoElement) => {
  try { stream?.getTracks?.().forEach(track => track.stop()); } catch (_) {}
  if (videoElement) {
    // A stale async camera request must never detach a newer stream that has
    // already been attached to the same <video> element.
    const attachedStream = videoElement.srcObject;
    if (!stream || attachedStream === stream) {
      try { videoElement.pause?.(); } catch (_) {}
      try { videoElement.srcObject = null; } catch (_) {}
    }
  }
};

export const openCameraStream = async ({
  videoElement,
  deviceId = '',
  facingMode = 'environment',
  width = 2560,
  height = 1920,
} = {}) => {
  const secureContext = typeof window !== 'undefined' && window.isSecureContext;
  const mediaAvailable = typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;
  if (!secureContext || !mediaAvailable) {
    const error = new Error('Camera API unavailable');
    error.name = !secureContext ? 'InsecureContextError' : 'NotSupportedError';
    throw error;
  }
  if (!videoElement) throw new Error('Video element unavailable');

  const preferredVideo = deviceId
    ? { deviceId: { exact: deviceId }, width: { ideal: width }, height: { ideal: height } }
    : { facingMode: { ideal: facingMode }, width: { ideal: width }, height: { ideal: height } };

  let stream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({ audio: false, video: preferredVideo });
  } catch (error) {
    if (error?.name === 'OverconstrainedError' || error?.name === 'ConstraintNotSatisfiedError') {
      stream = await navigator.mediaDevices.getUserMedia({ audio: false, video: true });
    } else {
      throw error;
    }
  }

  videoElement.srcObject = stream;
  try { await videoElement.play(); } catch (_) {}

  let devices = [];
  try {
    devices = (await navigator.mediaDevices.enumerateDevices()).filter(device => device.kind === 'videoinput');
  } catch (_) {}

  const videoTrack = stream.getVideoTracks?.()[0];
  return {
    stream,
    deviceId: videoTrack?.getSettings?.().deviceId || '',
    devices,
  };
};


const CAMERA_AUTO_MODE_KEYS = ['focusMode', 'exposureMode', 'whiteBalanceMode'];

const safeTrackSettings = track => {
  try { return track?.getSettings?.() || {}; } catch (_) { return {}; }
};

const safeTrackConstraints = track => {
  try { return track?.getConstraints?.() || {}; } catch (_) { return {}; }
};

export const prepareDocumentCamera = async (track) => {
  const initialSettings = safeTrackSettings(track);
  if (!track || track.readyState === 'ended' || typeof track.getCapabilities !== 'function' || typeof track.applyConstraints !== 'function') {
    return { capabilities: {}, settings: initialSettings, applied: [] };
  }

  let capabilities = {};
  try { capabilities = track.getCapabilities() || {}; } catch (_) {
    return { capabilities: {}, settings: initialSettings, applied: [] };
  }

  const applied = [];
  for (const key of CAMERA_AUTO_MODE_KEYS) {
    const supported = capabilities?.[key];
    if (!Array.isArray(supported) || !supported.includes('continuous')) continue;
    const currentSettings = safeTrackSettings(track);
    if (currentSettings?.[key] === 'continuous' || track.readyState === 'ended') continue;
    try {
      const existingConstraints = safeTrackConstraints(track);
      await track.applyConstraints({ ...existingConstraints, [key]: 'continuous' });
      if (safeTrackSettings(track)?.[key] === 'continuous') applied.push(key);
    } catch (_) {
      // Camera tuning is deliberately best-effort. Unsupported or partially
      // implemented controls must never make the existing camera unusable.
    }
  }

  return { capabilities, settings: safeTrackSettings(track), applied };
};

export const supportsCameraTorch = (track) => {
  try {
    const torch = track?.getCapabilities?.()?.torch;
    return torch === true || (Array.isArray(torch) && torch.includes(true));
  } catch (_) {
    return false;
  }
};

export const setCameraTorch = async (track, enabled) => {
  if (!track || track.readyState === 'ended' || typeof track.applyConstraints !== 'function' || !supportsCameraTorch(track)) {
    throw new Error('Torcia non supportata da questa fotocamera.');
  }

  const constraints = safeTrackConstraints(track);
  const advanced = Array.isArray(constraints.advanced) ? constraints.advanced : [];
  const withoutTorch = advanced.filter(item => !Object.prototype.hasOwnProperty.call(item || {}, 'torch'));
  await track.applyConstraints({
    ...constraints,
    advanced: [...withoutTorch, { torch: !!enabled }],
  });
  return !!enabled;
};

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

export const waitForDocumentCameraReady = async (videoElement, track, {
  warmupMs = 350,
  timeoutMs = 6000,
  pollMs = 50,
} = {}) => {
  const startedAt = Date.now();
  const isUsable = () => !!(
    videoElement?.srcObject &&
    videoElement.readyState >= 2 &&
    videoElement.videoWidth > 0 &&
    videoElement.videoHeight > 0
  );

  while (!isUsable()) {
    if (track?.readyState === 'ended') throw new Error('La fotocamera si è disconnessa durante l’avvio.');
    if (Date.now() - startedAt >= timeoutMs) throw new Error('La fotocamera non ha prodotto un’immagine utilizzabile.');
    await sleep(pollMs);
  }

  if (warmupMs > 0) await sleep(warmupMs);
  if (track?.readyState === 'ended' || !isUsable()) throw new Error('La fotocamera non è più disponibile.');
  return true;
};

export const captureVideoFrame = (videoElement, {
  maxDimension = 2560,
  mimeType = 'image/jpeg',
  quality = 0.9,
} = {}) => new Promise((resolve, reject) => {
  if (!videoElement?.videoWidth || !videoElement?.videoHeight) {
    reject(new Error('La fotocamera non è ancora pronta.'));
    return;
  }
  const scale = Math.min(1, maxDimension / Math.max(videoElement.videoWidth, videoElement.videoHeight));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(videoElement.videoWidth * scale));
  canvas.height = Math.max(1, Math.round(videoElement.videoHeight * scale));
  const context = canvas.getContext('2d');
  if (!context) {
    reject(new Error('Canvas non disponibile'));
    return;
  }
  context.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
  canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('Acquisizione immagine fallita')), mimeType, quality);
});

const getPhotoSettings = async (imageCapture, maxDimension, aspectRatio) => {
  if (typeof imageCapture?.getPhotoCapabilities !== 'function') return {};
  try {
    const capabilities = await imageCapture.getPhotoCapabilities();
    const maxWidth = Number(capabilities?.imageWidth?.max);
    const maxHeight = Number(capabilities?.imageHeight?.max);
    if (!Number.isFinite(maxWidth) || !Number.isFinite(maxHeight) || maxWidth <= 0 || maxHeight <= 0) return {};
    const targetAspect = Number.isFinite(aspectRatio) && aspectRatio > 0 ? aspectRatio : maxWidth / maxHeight;
    let targetWidth = maxWidth;
    let targetHeight = maxHeight;
    if (targetAspect > maxWidth / maxHeight) targetHeight = maxWidth / targetAspect;
    else targetWidth = maxHeight * targetAspect;
    const scale = Math.min(1, maxDimension / Math.max(targetWidth, targetHeight));
    return {
      imageWidth: Math.max(1, Math.round(targetWidth * scale)),
      imageHeight: Math.max(1, Math.round(targetHeight * scale)),
    };
  } catch (_) {
    return {};
  }
};

const loadPhotoImage = async (blob) => {
  if (typeof createImageBitmap === 'function') {
    const bitmap = await createImageBitmap(blob);
    return { image: bitmap, width: bitmap.width, height: bitmap.height, close: () => bitmap.close?.() };
  }

  if (typeof Image === 'undefined' || typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') {
    throw new Error('Lettura della foto non supportata.');
  }
  const url = URL.createObjectURL(blob);
  try {
    const image = await new Promise((resolve, reject) => {
      const element = new Image();
      element.onload = () => resolve(element);
      element.onerror = () => reject(new Error('Impossibile leggere la foto acquisita.'));
      element.src = url;
    });
    return { image, width: image.naturalWidth, height: image.naturalHeight, close: () => {} };
  } finally {
    URL.revokeObjectURL(url);
  }
};

const encodeJpeg = (canvas, quality) => new Promise((resolve, reject) => {
  canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('Preparazione della foto fallita.')), 'image/jpeg', quality);
});

const normalizeCapturedPhoto = async (blob, { maxDimension, maxFileSize, quality }) => {
  const decoded = await loadPhotoImage(blob);
  try {
    if (!decoded.width || !decoded.height) throw new Error('La foto acquisita non è valida.');
    const scale = Math.min(1, maxDimension / Math.max(decoded.width, decoded.height));
    const needsResize = scale < 1;
    const needsJpeg = blob.type !== 'image/jpeg';
    const needsSizeReduction = blob.size > maxFileSize;
    if (!needsResize && !needsJpeg && !needsSizeReduction) return blob;

    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(decoded.width * scale));
    canvas.height = Math.max(1, Math.round(decoded.height * scale));
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Canvas non disponibile.');
    context.drawImage(decoded.image, 0, 0, canvas.width, canvas.height);
    return await encodeJpeg(canvas, quality);
  } finally {
    decoded.close();
  }
};

export const captureDocumentPhoto = async (videoElement, track, {
  maxDimension = 2560,
  maxFileSize = 10 * 1024 * 1024,
  quality = 0.9,
  fallbackCapture = captureVideoFrame,
} = {}) => {
  const ImageCaptureConstructor = typeof window !== 'undefined' ? window.ImageCapture : undefined;
  if (typeof ImageCaptureConstructor !== 'function' || !track || track.readyState !== 'live') {
    return fallbackCapture(videoElement, { maxDimension, quality });
  }

  try {
    const imageCapture = new ImageCaptureConstructor(track);
    const videoAspectRatio = videoElement?.videoWidth && videoElement?.videoHeight
      ? videoElement.videoWidth / videoElement.videoHeight
      : undefined;
    const photoSettings = await getPhotoSettings(imageCapture, maxDimension, videoAspectRatio);
    let blob;
    try {
      blob = await imageCapture.takePhoto(photoSettings);
    } catch {
      if (!Object.keys(photoSettings).length) throw error;
      blob = await imageCapture.takePhoto();
    }
    if (!blob || typeof blob.size !== 'number') throw new Error('La fotocamera non ha restituito una foto valida.');
    return await normalizeCapturedPhoto(blob, { maxDimension, maxFileSize, quality });
  } catch (_) {
    // Keep the current video-frame capture as a safe path for drivers that cannot complete still capture.
    return fallbackCapture(videoElement, { maxDimension, quality });
  }
};
